import { useState, useCallback, useRef, useEffect } from 'react';
import { FunctionEntry, PhysicsObject, GraphSettings } from '@/types/simulation';
import { useTheme } from '@/contexts/ThemeContext';
import {
  renderToPNG, renderToJPG, renderToWebP,
  renderToSVG, renderToCanvas, renderToExcel, renderToVideo,
  ExportRenderOptions, TitleOptions,
} from '@/utils/exportRenderer';
import { exportToJSON } from '@/utils/fileImport';

interface Props {
  functions: FunctionEntry[];
  objects: PhysicsObject[];
  settings: GraphSettings;
  canvasWidth: number;
  canvasHeight: number;
  onClose: () => void;
}

type Format = 'png' | 'jpg' | 'webp' | 'svg' | 'json' | 'txt' | 'xlsx' | 'mp4';
type BgOption = 'theme' | '#ffffff' | '#000000' | 'transparent';
type ScaleOption = 1 | 2 | 3;
type Position = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

const BG_OPTIONS: { value: BgOption; label: string; preview: string }[] = [
  { value: 'theme',       label: 'テーマ色',    preview: 'bg-gradient-to-br from-[#0f0f1e] to-[#1a1a2e]' },
  { value: '#ffffff',     label: '白',          preview: 'bg-white border border-gray-200' },
  { value: '#000000',     label: '黒',          preview: 'bg-black' },
  { value: 'transparent', label: '透明',        preview: 'bg-[repeating-conic-gradient(#ccc_0%_25%,white_0%_50%)] bg-[length:12px_12px]' },
];

type FormatGroup = { group: string; formats: { value: Format; icon: string; label: string; desc: string }[] };

const FORMAT_GROUPS: FormatGroup[] = [
  {
    group: '画像',
    formats: [
      { value: 'png',  icon: 'ri-image-line',       label: 'PNG',  desc: '高品質・透明対応' },
      { value: 'jpg',  icon: 'ri-image-2-line',      label: 'JPG',  desc: '圧縮・軽量' },
      { value: 'webp', icon: 'ri-image-edit-line',   label: 'WebP', desc: '次世代フォーマット' },
      { value: 'svg',  icon: 'ri-file-code-line',    label: 'SVG',  desc: 'ベクター・拡大OK' },
    ],
  },
  {
    group: 'データ',
    formats: [
      { value: 'xlsx', icon: 'ri-file-excel-line',   label: 'Excel', desc: '表形式データ' },
      { value: 'json', icon: 'ri-braces-line',        label: 'JSON',  desc: 'データ保存' },
      { value: 'txt',  icon: 'ri-file-text-line',     label: 'TXT',   desc: 'テキスト形式' },
    ],
  },
  {
    group: '動画',
    formats: [
      { value: 'mp4',  icon: 'ri-video-line',         label: 'MP4',   desc: 'アニメーション録画' },
    ],
  },
];

const ALL_FORMATS = FORMAT_GROUPS.flatMap((g) => g.formats);

const POSITION_OPTIONS: { value: Position; label: string }[] = [
  { value: 'top-left',      label: '左上' },
  { value: 'top-center',    label: '上中央' },
  { value: 'top-right',     label: '右上' },
  { value: 'bottom-left',   label: '左下' },
  { value: 'bottom-center', label: '下中央' },
  { value: 'bottom-right',  label: '右下' },
];

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2 cursor-pointer group w-full text-left"
    >
      <div className={`relative w-8 h-4 rounded-full transition-colors duration-200 flex-shrink-0 ${checked ? 'bg-cyan-500/70' : 'bg-white/10'}`}>
        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
      <span className={`text-xs transition-colors ${checked ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}`}>{label}</span>
    </button>
  );
}

export default function ExportModal({ functions, objects, settings, canvasWidth, canvasHeight, onClose }: Props) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const textPrimary   = 'text-[var(--text-primary)]';
  const textMuted     = 'text-[var(--text-muted)]';
  const textSecondary = 'text-[var(--text-secondary)]';
  const borderSubtle  = 'border-[var(--border-subtle)]';

  const [format, setFormat] = useState<Format>('png');
  const [scale, setScale] = useState<ScaleOption>(2);
  const [bgColor, setBgColor] = useState<BgOption>('theme');
  const [exportTheme, setExportTheme] = useState<'dark' | 'light' | 'picture'>(
    theme === 'dark' ? 'dark' : theme === 'light' ? 'light' : 'picture'
  );
  const [jpgQuality, setJpgQuality] = useState(92);
  const [webpQuality, setWebpQuality] = useState(92);

  // MP4 settings
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoFps, setVideoFps] = useState(30);

  // Visibility toggles
  const [showGrid, setShowGrid] = useState(true);
  const [showAxisLabels, setShowAxisLabels] = useState(true);
  const [showTrails, setShowTrails] = useState(true);
  const [showObjects, setShowObjects] = useState(true);
  const [showObjectOrientation, setShowObjectOrientation] = useState(true);
  const [showVelocityArrow, setShowVelocityArrow] = useState(false);

  const [visibleFnIds, setVisibleFnIds] = useState<Set<string>>(
    () => new Set(functions.map((f) => f.id))
  );
  const [visibleObjIds, setVisibleObjIds] = useState<Set<string>>(
    () => new Set(objects.map((o) => o.id))
  );

  // Title settings
  const [showTitle, setShowTitle] = useState(false);
  const [titleText, setTitleText] = useState('');
  const [showDate, setShowDate] = useState(true);
  const [titlePosition, setTitlePosition] = useState<Position>('top-center');
  const [titleFontSize, setTitleFontSize] = useState(20);
  const [titleColor, setTitleColor] = useState('#000000');
  const [titleFontWeight, setTitleFontWeight] = useState<'normal' | 'bold' | 'light'>('bold');

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  const isImageFormat = format === 'png' || format === 'jpg' || format === 'webp' || format === 'svg';
  const isDataFormat  = format === 'xlsx' || format === 'json' || format === 'txt';
  const isVideoFormat = format === 'mp4';

  const buildOpts = useCallback((): ExportRenderOptions => {
    const title: TitleOptions | null = showTitle && (titleText.trim() || showDate)
      ? { text: titleText, showDate, position: titlePosition, fontSize: titleFontSize, color: titleColor, fontWeight: titleFontWeight }
      : null;
    return {
      functions, objects, settings,
      width: canvasWidth, height: canvasHeight,
      showGrid, showAxisLabels, showTrails, showObjects,
      showObjectOrientation, showVelocityArrow,
      visibleFunctionIds: visibleFnIds,
      visibleObjectIds: visibleObjIds,
      bgColor, theme: exportTheme, scale: 1, title,
    };
  }, [functions, objects, settings, canvasWidth, canvasHeight, showGrid, showAxisLabels, showTrails, showObjects, showObjectOrientation, showVelocityArrow, visibleFnIds, visibleObjIds, bgColor, exportTheme, showTitle, titleText, showDate, titlePosition, titleFontSize, titleColor, titleFontWeight]);

  // Live preview
  useEffect(() => {
    if (!isImageFormat) return;
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const opts = buildOpts();
    const previewW = 320;
    const previewH = Math.round(previewW * (canvasHeight / canvasWidth));
    const rendered = renderToCanvas({ ...opts, width: previewW, height: previewH, scale: 1 });
    canvas.width = previewW;
    canvas.height = previewH;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(rendered, 0, 0);
  }, [buildOpts, canvasWidth, canvasHeight, isImageFormat]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportDone(false);
    setVideoProgress(0);
    try {
      const opts = buildOpts();
      const ts = Date.now();

      if (format === 'png') {
        const blob = await renderToPNG({ ...opts, scale });
        downloadBlob(blob, `kuregraph_${ts}.png`);
      } else if (format === 'jpg') {
        const blob = await renderToJPG({ ...opts, scale }, jpgQuality / 100);
        downloadBlob(blob, `kuregraph_${ts}.jpg`);
      } else if (format === 'webp') {
        const blob = await renderToWebP({ ...opts, scale }, webpQuality / 100);
        downloadBlob(blob, `kuregraph_${ts}.webp`);
      } else if (format === 'svg') {
        const svg = renderToSVG(opts);
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        downloadBlob(blob, `kuregraph_${ts}.svg`);
      } else if (format === 'json') {
        const json = exportToJSON(functions.filter((f) => visibleFnIds.has(f.id)));
        const blob = new Blob([json], { type: 'application/json' });
        downloadBlob(blob, `kuregraph_${ts}.json`);
      } else if (format === 'txt') {
        const txt = functions.filter((f) => visibleFnIds.has(f.id)).map((f) => `y = ${f.expression}`).join('\n');
        const blob = new Blob([txt], { type: 'text/plain' });
        downloadBlob(blob, `kuregraph_${ts}.txt`);
      } else if (format === 'xlsx') {
        const trailData = objects
          .filter((o) => visibleObjIds.has(o.id) && o.trail.length > 0)
          .map((o) => ({ objectId: o.id, points: o.trail }));
        const blob = await renderToExcel({
          functions: functions.filter((f) => visibleFnIds.has(f.id)),
          objects: objects.filter((o) => visibleObjIds.has(o.id)),
          settings,
          trailData,
        });
        downloadBlob(blob, `kuregraph_${ts}.xlsx`);
      } else if (format === 'mp4') {
        const totalFrames = Math.round(videoDuration * videoFps);
        const { blob, ext } = await renderToVideo(
          { opts: { ...opts, scale: 1 }, durationSec: videoDuration, fps: videoFps, onProgress: setVideoProgress },
          (frameIndex) => {
            const t = frameIndex / totalFrames;
            const animOpts = { ...opts, scale: 1 };
            // 時間に応じてグラフを少しアニメーション（xMin/xMaxをゆっくりシフト）
            const shift = t * 2;
            return renderToCanvas({
              ...animOpts,
              settings: { ...settings, xMin: settings.xMin + shift, xMax: settings.xMax + shift },
            });
          }
        );
        downloadBlob(blob, `kuregraph_${ts}.${ext}`);
      }

      setExportDone(true);
      setTimeout(() => setExportDone(false), 2500);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
      setVideoProgress(0);
    }
  }, [buildOpts, format, scale, functions, objects, settings, visibleFnIds, visibleObjIds, jpgQuality, webpQuality, videoDuration, videoFps]);

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  const toggleFn = (id: string) => {
    setVisibleFnIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleObj = (id: string) => {
    setVisibleObjIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const currentFormatInfo = ALL_FORMATS.find((f) => f.value === format);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative glass rounded-2xl w-[860px] max-h-[92vh] flex flex-col animate-float-in overflow-hidden"
        style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${borderSubtle} flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.18), rgba(0,130,180,0.10))', border: '1px solid rgba(0,180,220,0.25)' }}>
              <i className="ri-export-line text-sm" style={{ color: 'var(--accent-cyan)' }} />
            </div>
            <div>
              <h2 className={`${textPrimary} text-sm font-bold`}>エクスポート</h2>
              <p className={`${textMuted} text-xs`}>形式・表示設定を選んでダウンロード</p>
            </div>
          </div>
          <button onClick={onClose} className={`glass-btn ripple-btn w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer ${textMuted}`}>
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">

          {/* LEFT: Settings */}
          <div className={`w-72 flex-shrink-0 flex flex-col overflow-y-auto custom-scrollbar border-r ${borderSubtle} p-4 space-y-5`}>

            {/* Format groups */}
            <div className="space-y-3">
              <p className="section-label">出力形式</p>
              {FORMAT_GROUPS.map((group) => (
                <div key={group.group} className="space-y-1.5">
                  <p className={`${textMuted} text-xs font-medium opacity-60`}>{group.group}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {group.formats.map(({ value, icon, label, desc }) => (
                      <button
                        key={value}
                        onClick={() => setFormat(value)}
                        className={`ripple-btn flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl cursor-pointer whitespace-nowrap transition-all text-center ${
                          format === value
                            ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
                            : `glass-btn ${textMuted}`
                        }`}
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${icon} text-base`} />
                        </div>
                        <span className="text-xs font-semibold">{label}</span>
                        <span className="text-xs opacity-60">{desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Image-specific settings */}
            {isImageFormat && (
              <>
                {(format === 'png' || format === 'jpg' || format === 'webp') && (
                  <div className="space-y-2">
                    <p className="section-label">解像度</p>
                    <div className="flex gap-1.5">
                      {([1, 2, 3] as ScaleOption[]).map((s) => (
                        <button
                          key={s}
                          onClick={() => setScale(s)}
                          className={`ripple-btn flex-1 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                            scale === s
                              ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
                              : `glass-btn ${textMuted}`
                          }`}
                        >
                          {s}x
                          <span className="block text-xs font-normal opacity-60">
                            {Math.round(canvasWidth * s)}×{Math.round(canvasHeight * s)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quality slider for JPG/WebP */}
                {(format === 'jpg' || format === 'webp') && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="section-label">品質</p>
                      <span className={`${textPrimary} text-xs font-mono`}>{format === 'jpg' ? jpgQuality : webpQuality}%</span>
                    </div>
                    <input
                      type="range" min={10} max={100}
                      value={format === 'jpg' ? jpgQuality : webpQuality}
                      onChange={(e) => format === 'jpg' ? setJpgQuality(Number(e.target.value)) : setWebpQuality(Number(e.target.value))}
                      className="w-full"
                    />
                    <div className={`flex justify-between ${textMuted} text-xs`}>
                      <span>軽量</span><span>高品質</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="section-label">背景</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {BG_OPTIONS.map(({ value, label, preview }) => (
                      <button
                        key={value}
                        onClick={() => setBgColor(value)}
                        className={`ripple-btn flex flex-col items-center gap-1 py-2 rounded-xl cursor-pointer transition-all ${
                          bgColor === value ? 'ring-2 ring-cyan-400/60' : 'ring-1 ring-white/10'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${preview}`} />
                        <span className={`text-xs ${textMuted}`}>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="section-label">カラーテーマ</p>
                  <div className="flex gap-1.5">
                    {(['dark', 'light', 'picture'] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setExportTheme(t)}
                        className={`ripple-btn flex-1 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                          exportTheme === t
                            ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
                            : `glass-btn ${textMuted}`
                        }`}
                      >
                        {t === 'dark' ? 'ダーク' : t === 'light' ? 'ライト' : '印刷'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <p className="section-label">タイトル / 日付</p>
                  <div className="glass rounded-xl p-3 space-y-3">
                    <Toggle checked={showTitle} onChange={setShowTitle} label="タイトルを表示" />
                    {showTitle && (
                      <>
                        <input
                          type="text"
                          value={titleText}
                          onChange={(e) => setTitleText(e.target.value)}
                          placeholder="タイトルを入力..."
                          className={`w-full bg-transparent ${textPrimary} text-sm outline-none border-b ${borderSubtle} pb-1.5 ${isDark ? 'placeholder-white/20' : 'placeholder-gray-300'}`}
                        />
                        <Toggle checked={showDate} onChange={setShowDate} label="日付を追加" />
                        <div className="space-y-1.5">
                          <p className={`${textMuted} text-xs`}>位置</p>
                          <div className="grid grid-cols-3 gap-1">
                            {POSITION_OPTIONS.map(({ value, label }) => (
                              <button
                                key={value}
                                onClick={() => setTitlePosition(value)}
                                className={`ripple-btn py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap transition-all ${
                                  titlePosition === value
                                    ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
                                    : `glass-btn ${textMuted}`
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <p className={`${textMuted} text-xs`}>フォントサイズ</p>
                            <span className={`${textPrimary} text-xs font-mono`}>{titleFontSize}px</span>
                          </div>
                          <input type="range" min={12} max={48} value={titleFontSize} onChange={(e) => setTitleFontSize(Number(e.target.value))} className="w-full" />
                        </div>
                        <div className="space-y-1.5">
                          <p className={`${textMuted} text-xs`}>文字色</p>
                          <div className="flex gap-1.5 flex-wrap">
                            {[
                              { color: '#000000', label: '黒' }, { color: '#ffffff', label: '白' },
                              { color: '#333333', label: '濃灰' }, { color: '#0070a0', label: '青' },
                              { color: '#c03a00', label: '赤' }, { color: '#2d7a2d', label: '緑' },
                            ].map(({ color, label }) => (
                              <button key={color} onClick={() => setTitleColor(color)}
                                className={`ripple-btn flex items-center gap-1 px-2 py-1 rounded-lg text-xs cursor-pointer whitespace-nowrap transition-all ${titleColor === color ? 'ring-1 ring-cyan-400/60' : ''}`}
                              >
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color, border: '1px solid rgba(255,255,255,0.2)' }} />
                                <span className={textMuted}>{label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <p className={`${textMuted} text-xs`}>太さ</p>
                          <div className="flex gap-1.5">
                            {(['light', 'normal', 'bold'] as const).map((w) => (
                              <button key={w} onClick={() => setTitleFontWeight(w)}
                                className={`ripple-btn flex-1 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap transition-all ${
                                  titleFontWeight === w
                                    ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
                                    : `glass-btn ${textMuted}`
                                }`}
                              >
                                {w === 'light' ? '細' : w === 'normal' ? '標準' : '太'}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Display toggles */}
                <div className="space-y-2">
                  <p className="section-label">表示設定</p>
                  <div className="glass rounded-xl p-3 space-y-2.5">
                    <Toggle checked={showGrid}              onChange={setShowGrid}              label="グリッド" />
                    <Toggle checked={showAxisLabels}        onChange={setShowAxisLabels}        label="軸ラベル" />
                    <Toggle checked={showTrails}            onChange={setShowTrails}            label="軌跡" />
                    <Toggle checked={showObjects}           onChange={setShowObjects}           label="物体" />
                    {showObjects && (
                      <div className="pl-4 space-y-2">
                        <Toggle checked={showObjectOrientation} onChange={setShowObjectOrientation} label="物体の向き" />
                        <Toggle checked={showVelocityArrow}     onChange={setShowVelocityArrow}     label="速度ベクトル" />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* MP4 settings */}
            {isVideoFormat && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="section-label">録画時間</p>
                    <span className={`${textPrimary} text-xs font-mono`}>{videoDuration}秒</span>
                  </div>
                  <input type="range" min={1} max={30} value={videoDuration} onChange={(e) => setVideoDuration(Number(e.target.value))} className="w-full" />
                  <div className={`flex justify-between ${textMuted} text-xs`}><span>1秒</span><span>30秒</span></div>
                </div>
                <div className="space-y-2">
                  <p className="section-label">フレームレート</p>
                  <div className="flex gap-1.5">
                    {[24, 30, 60].map((fps) => (
                      <button key={fps} onClick={() => setVideoFps(fps)}
                        className={`ripple-btn flex-1 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                          videoFps === fps
                            ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
                            : `glass-btn ${textMuted}`
                        }`}
                      >
                        {fps}fps
                      </button>
                    ))}
                  </div>
                </div>
                <div className="glass rounded-xl p-3 space-y-2">
                  <p className={`${textMuted} text-xs`}>
                    <i className="ri-information-line mr-1" />
                    グラフをゆっくりスクロールしながら録画します。シミュレーション中に録画すると物体の動きも含まれます。
                  </p>
                  <p className={`${textMuted} text-xs`}>
                    ブラウザによっては <strong className={textSecondary}>.webm</strong> 形式になる場合があります。
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="section-label">表示設定</p>
                  <div className="glass rounded-xl p-3 space-y-2.5">
                    <Toggle checked={showGrid}        onChange={setShowGrid}        label="グリッド" />
                    <Toggle checked={showAxisLabels}  onChange={setShowAxisLabels}  label="軸ラベル" />
                    <Toggle checked={showObjects}     onChange={setShowObjects}     label="物体" />
                  </div>
                </div>
              </div>
            )}

            {/* Excel info */}
            {format === 'xlsx' && (
              <div className="space-y-2">
                <p className="section-label">出力内容</p>
                <div className="glass rounded-xl p-3 space-y-2">
                  {[
                    { icon: 'ri-function-line', label: '関数シート', desc: `${functions.filter(f => visibleFnIds.has(f.id)).length}件の関数` },
                    { icon: 'ri-circle-line', label: '物体シート', desc: `${objects.filter(o => visibleObjIds.has(o.id)).length}件の物体` },
                    { icon: 'ri-settings-3-line', label: '設定シート', desc: 'グラフ範囲など' },
                    { icon: 'ri-route-line', label: '軌跡シート', desc: `${objects.filter(o => visibleObjIds.has(o.id) && o.trail.length > 0).length}件の軌跡データ` },
                  ].map(({ icon, label, desc }) => (
                    <div key={label} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`${icon} text-xs`} style={{ color: 'var(--accent-cyan)' }} />
                      </div>
                      <span className={`${textPrimary} text-xs`}>{label}</span>
                      <span className={`${textMuted} text-xs ml-auto`}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-function visibility */}
            {functions.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="section-label">関数</p>
                  <div className="flex gap-1">
                    <button onClick={() => setVisibleFnIds(new Set(functions.map(f => f.id)))} className={`glass-btn ripple-btn text-xs ${textMuted} px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap`}>全ON</button>
                    <button onClick={() => setVisibleFnIds(new Set())} className={`glass-btn ripple-btn text-xs ${textMuted} px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap`}>全OFF</button>
                  </div>
                </div>
                <div className="space-y-1">
                  {functions.map((fn) => (
                    <button key={fn.id} onClick={() => toggleFn(fn.id)}
                      className={`glass ripple-btn w-full flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition-all text-left ${visibleFnIds.has(fn.id) ? '' : 'opacity-40'}`}
                    >
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: fn.color }} />
                      <span className={`${textPrimary} text-xs font-mono flex-1 min-w-0 truncate`}>y = {fn.expression || '(空)'}</span>
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`text-xs ${visibleFnIds.has(fn.id) ? 'ri-eye-line text-cyan-400' : 'ri-eye-off-line text-white/20'}`} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Per-object visibility */}
            {objects.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="section-label">物体</p>
                  <div className="flex gap-1">
                    <button onClick={() => setVisibleObjIds(new Set(objects.map(o => o.id)))} className={`glass-btn ripple-btn text-xs ${textMuted} px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap`}>全ON</button>
                    <button onClick={() => setVisibleObjIds(new Set())} className={`glass-btn ripple-btn text-xs ${textMuted} px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap`}>全OFF</button>
                  </div>
                </div>
                <div className="space-y-1">
                  {objects.map((obj, i) => (
                    <button key={obj.id} onClick={() => toggleObj(obj.id)}
                      className={`glass ripple-btn w-full flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition-all text-left ${visibleObjIds.has(obj.id) ? '' : 'opacity-40'}`}
                    >
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: obj.color }} />
                      <span className={`${textPrimary} text-xs flex-1`}>物体 {i + 1}<span className={`${textMuted} ml-1`}>({obj.shape})</span></span>
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`text-xs ${visibleObjIds.has(obj.id) ? 'ri-eye-line text-cyan-400' : 'ri-eye-off-line text-white/20'}`} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Preview + Export */}
          <div className="flex-1 flex flex-col p-4 gap-4 overflow-hidden">
            <div className="flex-1 flex flex-col gap-2 min-h-0">
              <div className="flex items-center justify-between">
                <p className="section-label">プレビュー</p>
                {isImageFormat && format !== 'svg' && (
                  <span className={`${textMuted} text-xs`}>
                    {Math.round(canvasWidth * scale)} × {Math.round(canvasHeight * scale)}px
                  </span>
                )}
                {format === 'svg' && <span className={`${textMuted} text-xs`}>ベクター（無限解像度）</span>}
              </div>
              <div className="flex-1 glass rounded-xl overflow-hidden flex items-center justify-center min-h-0">
                {isImageFormat ? (
                  <canvas ref={previewCanvasRef} className="max-w-full max-h-full object-contain" style={{ imageRendering: 'pixelated' }} />
                ) : (
                  <div className="flex flex-col items-center gap-4 p-8 text-center">
                    <div className="w-20 h-20 flex items-center justify-center rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.15), rgba(0,130,180,0.08))', border: '1px solid rgba(0,180,220,0.20)' }}>
                      <i className={`${currentFormatInfo?.icon} text-4xl`} style={{ color: 'var(--accent-cyan)' }} />
                    </div>
                    <div>
                      <p className={`${textPrimary} text-base font-bold`}>{currentFormatInfo?.label} 形式</p>
                      <p className={`${textMuted} text-sm mt-1`}>{currentFormatInfo?.desc}</p>
                    </div>
                    {format === 'xlsx' && (
                      <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
                        {[
                          { label: '関数', count: functions.filter(f => visibleFnIds.has(f.id)).length, icon: 'ri-function-line' },
                          { label: '物体', count: objects.filter(o => visibleObjIds.has(o.id)).length, icon: 'ri-circle-line' },
                        ].map(({ label, count, icon }) => (
                          <div key={label} className="glass rounded-xl p-3 flex flex-col items-center gap-1">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={`${icon} text-sm`} style={{ color: 'var(--accent-cyan)' }} />
                            </div>
                            <span className={`${textPrimary} text-lg font-bold`}>{count}</span>
                            <span className={`${textMuted} text-xs`}>{label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {format === 'mp4' && (
                      <div className="glass rounded-xl p-4 w-full max-w-xs space-y-2">
                        <div className="flex justify-between">
                          <span className={`${textMuted} text-xs`}>録画時間</span>
                          <span className={`${textPrimary} text-xs font-mono`}>{videoDuration}秒</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={`${textMuted} text-xs`}>フレームレート</span>
                          <span className={`${textPrimary} text-xs font-mono`}>{videoFps}fps</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={`${textMuted} text-xs`}>総フレーム数</span>
                          <span className={`${textPrimary} text-xs font-mono`}>{videoDuration * videoFps}枚</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Video progress bar */}
            {isVideoFormat && exporting && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className={`${textMuted} text-xs`}>録画中...</span>
                  <span className={`${textPrimary} text-xs font-mono`}>{Math.round(videoProgress * 100)}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-200"
                    style={{ width: `${videoProgress * 100}%`, background: 'linear-gradient(90deg, rgba(0,180,220,0.8), rgba(0,130,180,0.6))' }}
                  />
                </div>
              </div>
            )}

            {/* Export button */}
            <button
              onClick={handleExport}
              disabled={exporting}
              className={`ripple-btn w-full py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap text-white transition-all flex items-center justify-center gap-2 ${
                exportDone
                  ? 'bg-emerald-500/70 border border-emerald-400/50'
                  : 'hover:scale-[1.02] active:scale-[0.98]'
              } disabled:opacity-50`}
              style={exportDone ? {} : { background: 'linear-gradient(135deg, rgba(0,180,220,0.75), rgba(0,130,180,0.65))', border: '1px solid rgba(0,180,220,0.45)' }}
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line text-sm animate-spin" />
                  </div>
                  {isVideoFormat ? `録画中... ${Math.round(videoProgress * 100)}%` : '生成中...'}
                </>
              ) : exportDone ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-sm" />
                  </div>
                  ダウンロード完了！
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-download-2-line text-sm" />
                  </div>
                  {currentFormatInfo?.label} でダウンロード
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
